import React from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";

import { BaseProcedureType } from "@graffer-inc/cloud-gov-core";
import {
  CreateKintoneExportSettingInput,
  gqlClient,
  gqlHeader,
  reportUnexpectedError,
  runGqlQueryV2,
} from "@graffer-inc/smart-apply";

import smartApplyApiClient from "../../../../../../application/apis/api-client";
import {
  parseKintoneExportSettingCreateError,
  parseError,
  validateCreateSettingInput,
} from "../../../../../../application/domain/kintone-export";

type Props<TENANT_ID extends string | undefined> = {
  tenantId: TENANT_ID;

  detailLinkProvider: (arg: {
    tenantId: TENANT_ID;
    kintoneExportSettingId: string;
  }) => string;
};

export const useKintoneExportCreatePage = <
  TENANT_ID extends string | undefined,
>({
  tenantId,
  detailLinkProvider,
}: Props<TENANT_ID>) => {
  const [input, setInput] = React.useState<CreateKintoneExportSettingInput>({
    tenantId: tenantId ?? "",
    formResourceId: "",

    kintoneAppId: "",
    kintoneAPIToken: "",
    kintoneSubdomain: "",

    disabled: true,
    name: "",
    description: "",

    elementFieldMappings: [],
    metadataFieldMapping: {
      submissionIdFieldCode: "",
      submittedAtFieldCode: "",
    },
  });

  React.useEffect(() => {
    if (!tenantId) {
      return;
    }
    setInput((prev) => ({ ...prev, tenantId }));
  }, [tenantId, setInput]);

  const [selectedSmartApplyProcedure, setSelectedSmartApplyProcedure] =
    React.useState<BaseProcedureType>();
  React.useEffect(() => {
    if (input.formResourceId) {
      smartApplyApiClient
        .fetchProcedure(input.formResourceId)
        .then(({ data }) => {
          setSelectedSmartApplyProcedure(data);
        })
        .catch((e) => {
          toast.error("スマート申請手続きの取得に失敗しました。");
          reportUnexpectedError(e);
        });
    }
  }, [input.formResourceId, setSelectedSmartApplyProcedure]);

  const kintoneAppUrl = React.useMemo(() => {
    return `https://${input.kintoneSubdomain}.cybozu.com/k/${input.kintoneAppId}/`;
  }, [input]);

  const [isLoading, setLoading] = React.useState(false);
  const [showDialog, setShowDialog] = React.useState(false);
  const [showValidationErrorMessage, setShowValidationErrorMessage] =
    React.useState(false);
  const [showKintoneApiErrorMessage, setShowKintoneApiErrorMessage] =
    React.useState(false);
  const [
    showNoEmailUserInGroupErrorMessage,
    setShowNoEmailUserInGroupErrorMessage,
  ] = React.useState(false);
  const history = useHistory();

  const onClickCreateOnDialog = React.useCallback(async () => {
    setLoading(true);
    setShowKintoneApiErrorMessage(false);

    // Dialog の button に対して disabled を設定できないので
    // dialog を閉じて二重送信を予防する。
    setShowDialog(false);

    try {
      const res = await runGqlQueryV2(() => {
        return gqlClient.createKintoneExportSetting({ input }, gqlHeader("m"));
      });

      if (res.error) {
        throw res.error.errors.length === 1
          ? res.error.errors[0]
          : res.error.rawError;
      }

      history.push(
        detailLinkProvider({
          tenantId,
          kintoneExportSettingId: res.data!.createKintoneExportSetting.id,
        }),
      );
    } catch (error) {
      if (parseError(error)) {
        setShowKintoneApiErrorMessage(true);
        return;
      }

      if (parseKintoneExportSettingCreateError(error)) {
        setShowNoEmailUserInGroupErrorMessage(true);
        return;
      }

      reportUnexpectedError(error);
      toast.error("連携設定の作成に失敗しました。");
    } finally {
      setLoading(false);
    }
  }, [
    tenantId,
    input,
    history,
    setLoading,
    setShowDialog,
    setShowKintoneApiErrorMessage,
  ]);

  const onClickCreateOnPage = React.useCallback(() => {
    if (validateCreateSettingInput(input)) {
      setShowDialog(true);
    } else {
      setShowValidationErrorMessage(true);
    }
  }, [setShowDialog, setShowValidationErrorMessage, input]);

  React.useEffect(() => {
    if (validateCreateSettingInput(input)) {
      setShowValidationErrorMessage(false);
    }
  }, [input, setShowValidationErrorMessage]);

  const onClickCancelOnDialog = React.useCallback(() => {
    setShowDialog(false);
  }, [setShowDialog]);

  return {
    input,
    setInput,
    selectedSmartApplyProcedure,
    kintoneAppUrl,
    isLoading,
    showDialog,
    showValidationErrorMessage,
    showKintoneApiErrorMessage,
    showNoEmailUserInGroupErrorMessage,
    onClickCreateOnPage,
    onClickCreateOnDialog,
    onClickCancelOnDialog,
  };
};
